import React, { useState } from 'react';

import cn from 'classnames';
import { MdContentPaste } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { programSel } from '@context/duck/selectorsTyped';
import {
  navigateToBulkAwards,
  navigateToBulkEntryAdventure,
  navigateToBulkEntryMB,
} from '@modules/bulkEntry/duck/actions';
import {
  shouldShowAdventuresBulkEntry,
  shouldShowAwardsBulkEntry,
  shouldShowMeritBadgeBulkEntry,
  shouldShowRankBulkEntry,
  shouldShowYouthLeadershipBulkEntry,
} from '@modules/bulkEntry/duck/utils/featureFlags';
import { navigateToBulkRemoveEvents } from '@modules/events/duck/actionsTyped';
import { OfflineTooltip } from '@offline';
import {
  navigateToQuickEntryCalendar,
  navigateToQuickEntryScoutLeadership,
} from '@shared';
import { Button, Dropdown, ExpandToggle, Menu } from '@shared/components';
import { Program } from '@shared/constants';

import { currentUnitGuidSel } from '../../../../unit/duck';
import {
  navigateToQuickEntryHealth,
  navigateToQuickEntryOA,
  navigateToQuickEntryRank,
  navigateToQuickEntrySchool,
  navigateToQuickEntrySwimming,
} from '../../actions';
import styles from './QuickEntryDropdown.less';

enum MenuItems {
  deleteEvents = 'deleteEvents',
  heathRecord = 'heathRecord',
  swimmerClass = 'swimmerClass',
  schoolInfo = 'schoolInfo',
  oaMember = 'oaMember',
  qeRank = 'qeRank',
  bulkEntryMB = 'bulkEntryMB',
  bulkEntryAdventures = 'bulkEntryAdventures',
  bulkEntryAwards = 'bulkEntryAwards',
  scoutLeadership = 'scoutLeadership',
  calendar = 'calendar',
}

const MenuItem = Menu.Item;
const MenuDivider = Menu.Divider;

function shouldShowOA({ unitProgram }: { unitProgram: string }) {
  if (unitProgram === Program.CUB_SCOUT) {
    return false;
  }

  return true;
}

const QuickEntryDropdown = ({ disabled }: { disabled?: boolean }) => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const unitGuid = useSelector(currentUnitGuidSel);
  const unitProgram = useSelector(programSel);

  const handleVisibleChange = (visible: boolean) => {
    setVisible(visible);
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    setVisible(false);

    switch (key) {
      case MenuItems.heathRecord: {
        dispatch(navigateToQuickEntryHealth());
        return;
      }
      case MenuItems.swimmerClass: {
        dispatch(navigateToQuickEntrySwimming());
        return;
      }
      case MenuItems.schoolInfo: {
        dispatch(navigateToQuickEntrySchool());
        return;
      }
      case MenuItems.oaMember: {
        dispatch(navigateToQuickEntryOA());
        return;
      }

      case MenuItems.qeRank: {
        dispatch(navigateToQuickEntryRank({ unitGuid }));
        return;
      }

      case MenuItems.scoutLeadership: {
        dispatch(navigateToQuickEntryScoutLeadership());
        return;
      }

      case MenuItems.bulkEntryMB: {
        dispatch(navigateToBulkEntryMB({ unitGuid }));
        return;
      }

      case MenuItems.bulkEntryAdventures: {
        dispatch(navigateToBulkEntryAdventure({ unitGuid }));
        return;
      }
      case MenuItems.calendar: {
        dispatch(navigateToQuickEntryCalendar());
        return;
      }
      case MenuItems.deleteEvents: {
        dispatch(navigateToBulkRemoveEvents());
        return;
      }
      case MenuItems.bulkEntryAwards: {
        dispatch(navigateToBulkAwards({ unitGuid }));
        return;
      }

      default: {
        throw new Error(`Unknown report type: ${key}`);
      }
    }
  };

  const buttons = (
    <Menu onClick={handleMenuClick}>
      {shouldShowRankBulkEntry(unitProgram) && (
        <MenuItem key={MenuItems.qeRank}>
          <FormattedMessage id="packRoster.QuickEntryDropdown.qeRank" />
        </MenuItem>
      )}
      {shouldShowRankBulkEntry(unitProgram) ? <MenuDivider /> : undefined}

      {shouldShowMeritBadgeBulkEntry(unitProgram) ? (
        <MenuItem key={MenuItems.bulkEntryMB}>
          <FormattedMessage id="packRoster.QuickEntryDropdown.bulkEntryMB" />
        </MenuItem>
      ) : undefined}
      {shouldShowMeritBadgeBulkEntry(unitProgram) ? <MenuDivider /> : undefined}

      {shouldShowAdventuresBulkEntry(unitProgram) ? (
        <MenuItem key={MenuItems.bulkEntryAdventures}>
          <FormattedMessage id="packRoster.QuickEntryDropdown.bulkEntryAdventures" />
        </MenuItem>
      ) : undefined}
      {shouldShowAdventuresBulkEntry(unitProgram) ? <MenuDivider /> : undefined}

      {shouldShowAwardsBulkEntry(unitProgram) ? (
        <MenuItem key={MenuItems.bulkEntryAwards}>
          <FormattedMessage id="packRoster.QuickEntryDropdown.bulkEntryAwards" />
        </MenuItem>
      ) : undefined}
      {shouldShowAwardsBulkEntry(unitProgram) ? <MenuDivider /> : undefined}
      <MenuItem key={MenuItems.deleteEvents}>
        <FormattedMessage id="packRoster.QuickEntryDropdown.qeDeleteEvents" />
      </MenuItem>
      <MenuDivider />
      <MenuItem key={MenuItems.heathRecord}>
        <FormattedMessage id="packRoster.QuickEntryDropdown.heathRecord" />
      </MenuItem>
      <MenuDivider />
      <MenuItem key={MenuItems.swimmerClass}>
        <FormattedMessage id="packRoster.QuickEntryDropdown.swimmerClass" />
      </MenuItem>
      {shouldShowYouthLeadershipBulkEntry(unitProgram) && <MenuDivider />}
      {shouldShowYouthLeadershipBulkEntry(unitProgram) && (
        <MenuItem key={MenuItems.scoutLeadership}>
          <FormattedMessage id="packRoster.QuickEntryDropdown.scoutLeadership" />
        </MenuItem>
      )}

      {shouldShowOA({ unitProgram }) ? <MenuDivider /> : undefined}
      {shouldShowOA({ unitProgram }) ? (
        <MenuItem key={MenuItems.oaMember}>
          <FormattedMessage id="packRoster.QuickEntryDropdown.OaData" />
        </MenuItem>
      ) : undefined}
      <MenuDivider />
      <MenuItem key={MenuItems.schoolInfo}>
        <FormattedMessage id="packRoster.QuickEntryDropdown.schoolInfo" />
      </MenuItem>
    </Menu>
  );

  return (
    <OfflineTooltip placement="top">
      <Dropdown
        overlay={buttons}
        trigger={['click']}
        placement="bottomRight"
        visible={visible}
        disabled={disabled}
        onVisibleChange={handleVisibleChange}
      >
        <Button
          className={cn(
            {
              [styles.disabled]: disabled,
            },
            styles.toggleButton,
          )}
          ghost
          noBorder
          color="white"
          size="small"
          uppercase={false}
        >
          <MdContentPaste className={styles.reportIcon} />
          <FormattedMessage id="packRoster.QuickEntryDropdown.quickEntry" />
          <ExpandToggle
            className={styles.arrow}
            expanded={visible && !disabled}
          />
        </Button>
      </Dropdown>
    </OfflineTooltip>
  );
};

export default QuickEntryDropdown;
